/**
 * ScChoiceCard (026, US3) — radio-style option picker with a confirm button.
 * Internal selection state via useState. Fires onAction('confirm', { selected_id })
 * on confirm tap. IN_CARD_ACTION widget — confirm button renders inside the card.
 */

import { useState } from 'react';

import { cn } from '@/lib/utils';
import { ChatCard, WidgetActionProps } from '../../widgets/chat-widget-ui';
import { capitalizeFirst } from '../../widgets/textFormat';
import { SkBar, SkBtn } from '../../widgets/skeletons';
import { ScStateBadge } from './ScStateBadge';
import type { ScChoiceCardProps } from '@shared/widget-schemas';

type ScChoiceCardComponentProps = ScChoiceCardProps & Pick<WidgetActionProps, 'onAction'> & {
  className?: string;
};

export function ScChoiceCard({
  title,
  prompt,
  options,
  defaultId,
  confirmLabel = 'Confirm',
  draft,
  onAction,
  className,
}: ScChoiceCardComponentProps) {
  const validDefaultId = defaultId && options.some((o) => o.id === defaultId) ? defaultId : null;
  const [selected, setSelected] = useState<string | null>(validDefaultId);

  function handleConfirm() {
    if (selected) {
      onAction?.('confirm', { selected_id: selected });
    }
  }

  return (
    <ChatCard className={cn('flex flex-col gap-3 p-4', className)}>
      {/* Header */}
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground font-mono text-[10.5px] font-bold uppercase tracking-wide">
            Choose
          </span>
          <ScStateBadge draft={draft} />
        </div>
        <p className="text-foreground text-[13.5px] font-semibold leading-snug">{capitalizeFirst(title)}</p>
        {prompt && (
          <p className="text-muted-foreground text-[12px]">{capitalizeFirst(prompt)}</p>
        )}
      </div>

      {/* Options */}
      <ul className="flex flex-col gap-2" role="listbox" aria-label={title}>
        {options.map((option) => {
          const isSelected = selected === option.id;
          return (
            <li key={option.id}>
              <button
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => setSelected(option.id)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors',
                  isSelected
                    ? 'border-[#7C3AED]/50 bg-[#7C3AED]/8'
                    : 'border-[var(--border)] bg-[var(--secondary)] hover:border-[var(--border-strong)]',
                )}
              >
                <span
                  className={cn(
                    'inline-flex size-5 shrink-0 items-center justify-center rounded-full border-2',
                    isSelected ? 'border-[#7C3AED]' : 'border-[var(--border)]',
                  )}
                >
                  {isSelected && (
                    <span className="size-2.5 rounded-full bg-[#7C3AED]" />
                  )}
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className={cn('text-[13px] font-medium', isSelected && 'text-[#C084FC]')}>
                    {capitalizeFirst(option.label)}
                  </span>
                  {option.sub && (
                    <span className="text-muted-foreground text-[11px]">{capitalizeFirst(option.sub)}</span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Confirm button — disabled until selection made */}
      <button
        type="button"
        disabled={!selected}
        onClick={handleConfirm}
        className={cn(
          'inline-flex w-full items-center justify-center rounded-[11px] px-5 py-[11px] text-[13.5px] font-semibold transition-opacity',
          selected
            ? 'text-white'
            : 'cursor-not-allowed opacity-40',
        )}
        style={selected ? { background: 'var(--gradient-primary)', boxShadow: '0 6px 18px rgba(124,58,237,0.32)' } : { background: 'var(--secondary)', border: '1px solid var(--border)' }}
      >
        {capitalizeFirst(confirmLabel)}
      </button>
    </ChatCard>
  );
}

export function ScChoiceCardSkeleton({ className }: { className?: string }) {
  return (
    <ChatCard className={cn('flex flex-col gap-3 p-4', className)}>
      <SkBar width="35%" height="10px" />
      <SkBar width="70%" height="13px" />
      {[1, 2, 3].map((i) => (
        <SkBar key={i} height="48px" radius="12px" />
      ))}
      <SkBtn width="100%" height="42px" />
    </ChatCard>
  );
}
